import api from './api';
import { Team } from './teams';

export interface CustomerFamily {
  id: number;
  customer_id: number;
  type: 'spouse' | 'children';
  name: string;
  identification_no?: string;
  attachment?: string;
}

export interface CustomerAsset {
  id: number;
  customer_id: number;
  category: 'existing' | 'planning';
  type: 'motor_vehicle' | 'property';
  plate_no?: string;
  model?: string;
  address?: string;
  year?: number;
  price?: number;
  installment_no?: number;
  installment?: number;
}

export interface CustomerReporting {
  id: number;
  customer_id: number;
  identification_passport?: string;
  visa?: string; // Assessment selection
  visa_text?: string; // Assessment text value
  bank?: string; // File path (Supporting tab)
  bank_text?: string; // Text input (Overview tab)
  address?: string;
  signature?: string;
  tax?: string;
}

export interface CustomerContact {
  id: number;
  customer_id: number;
  contact_number?: string;
  email?: string;
  address1?: string;
  address2?: string;
}

export interface Bank {
  id: number;
  name: string;
  enabled: boolean;
}

export interface CustomerFinancial {
  id: number;
  customer_id: number;
  bank_id?: number;
  branch_name?: string;
  bank_number?: string;
  bank?: Bank;
}

export interface User {
  id: number;
  name: string;
  email?: string;
}

export interface CustomerChangeRequest {
  id: number;
  customer_id: number;
  field_name: string;
  old_value?: string;
  new_value: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by?: number;
  requested_at?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
  requester?: User;
  reviewer?: User;
}

export interface Customer {
  id: number;
  name: string;
  type?: 'potential' | 'active' | 'inactive';
  email?: string;
  password?: string;
  lhdn_password?: string;
  ic_passport_no?: string;
  tin_no?: string;
  nationality?: string;
  race?: string;
  sex?: string;
  engagement?: string;
  agent_id?: number;
  agent_staff_id?: number;
  agent_appoint_date?: string;
  agent_resign_date?: string;
  staff_id?: number;
  appoint_date?: string;
  resign_date?: string;
  team_id?: number;
  tax_date?: string;
  enabled: boolean;
  is_change_password?: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  agent?: User;
  team?: Team;
  family?: CustomerFamily[];
  assets?: CustomerAsset[];
  reporting?: CustomerReporting;
  contacts?: CustomerContact[];
  financials?: CustomerFinancial[];
  change_requests?: CustomerChangeRequest[];
}

export interface CreateCustomerFamilyData {
  type: 'spouse' | 'children';
  name: string;
  identification_no?: string;
  attachment?: string;
}

export interface CreateCustomerAssetData {
  category: 'existing' | 'planning';
  type: 'motor_vehicle' | 'property';
  plate_no?: string;
  model?: string;
  address?: string;
  year?: number;
  price?: number;
  installment_no?: number;
  installment?: number;
}

export interface CreateCustomerContactData {
  contact_number?: string;
  email?: string;
  address1?: string;
  address2?: string;
}

export interface CreateCustomerFinancialData {
  bank_id?: number;
  branch_name?: string;
  bank_number?: string;
}

export interface CreateCustomerData {
  name: string;
  type?: 'potential' | 'active' | 'inactive';
  email?: string;
  password?: string;
  lhdn_password?: string;
  ic_passport_no?: string;
  tin_no?: string;
  nationality?: string;
  race?: string;
  sex?: string;
  engagement?: string;
  agent_id?: number;
  agent_staff_id?: number;
  agent_appoint_date?: string;
  agent_resign_date?: string;
  staff_id?: number;
  appoint_date?: string;
  resign_date?: string;
  team_id?: number;
  tax_date?: string;
  enabled?: boolean;
  family?: CreateCustomerFamilyData[];
  assets?: CreateCustomerAssetData[];
  contacts?: CreateCustomerContactData[];
  financials?: CreateCustomerFinancialData[];
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const customerService = {
  async getAll(params?: { page?: number; per_page?: number; search?: string }) {
    const response = await api.get('/customers', { params });
    return response.data;
  },

  async getById(id: number): Promise<Customer> {
    const response = await api.get(`/customers/${id}`);
    return unwrap<Customer>(response);
  },

  async create(data: CreateCustomerData | FormData): Promise<Customer> {
    const response = await api.post('/customers', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return unwrap<Customer>(response);
  },

  async update(id: number, data: Partial<CreateCustomerData> | FormData): Promise<Customer> {
    // For FormData, use POST with _method=PUT because Laravel has issues parsing files in PUT requests
    if (data instanceof FormData) {
      // Append _method=PUT for method spoofing
      data.append('_method', 'PUT');
      
      console.log('=== API Request Config ===');
      console.log('Is FormData: true');
      console.log('Using POST with _method=PUT for file uploads');
      
      // Use POST instead of PUT for FormData
      const response = await api.post(`/customers/${id}`, data);
      return unwrap<Customer>(response);
    } else {
      // For JSON data, use PUT as normal
      const response = await api.put(`/customers/${id}`, data);
    return unwrap<Customer>(response);
    }
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/customers/${id}`);
  },

  async search(query: string) {
    const response = await api.get(`/customers/search/${query}`);
    return response.data;
  },
  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async downloadImportTemplate() {
    const response = await api.get('/customers/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async approveChangeRequest(id: number) {
    const response = await api.post(`/customer-change-requests/${id}/approve`);
    return response.data;
  },

  async rejectChangeRequest(id: number) {
    const response = await api.post(`/customer-change-requests/${id}/reject`);
    return response.data;
  },

  async createChangeRequest(data: {
    customer_id: number;
    field_name: string;
    old_value: string;
    new_value: string;
    status?: 'pending' | 'approved' | 'rejected';
  }) {
    const response = await api.post('/customer-change-requests', data);
    return response.data;
  },
};
